// ChallengeCheckoutPage — Step 4: review + pay
// iOS HIG: inset-grouped order summary + form fields, bottom toolbar with Pay button

import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChallengeLayout, IOSCheck, IOSList, IOSPrimaryButton, IOSRow, IOSSectionHeader, IOSSecondaryButton,
} from './ChallengeLayout';
import { clearDraft, loadDraft, type PlatformKey, type SizeKey, type TierName } from './store';
import { getPlatformOption, getSizeOption } from './data';
import { API } from '../../../lib/api';
import { getAuth } from '../../../lib/auth';

export function ChallengeCheckoutPage() {
  const nav = useNavigate();
  const draft = loadDraft();
  const tier: TierName | undefined = draft.tier;
  const size: SizeKey | undefined = draft.size;
  const platform: PlatformKey | undefined = draft.platform;

  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExp, setCardExp] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ orderId?: string; tier?: string; size?: string; platform?: string } | null>(null);

  useEffect(() => {
    if (done) return;
    if (!tier) nav('/portal/challenge/tier', { replace: true });
    else if (!size) nav('/portal/challenge/size', { replace: true });
    else if (!platform) nav('/portal/challenge/platform', { replace: true });
  }, [tier, size, platform, nav, done]);

  if (done) {
    return (
      <div className="min-h-screen bg-[#F2F2F7] text-[#1C1C1E] antialiased">
        <header className="sticky top-0 z-40 border-b border-[#C6C6C8]/60 bg-white/85 backdrop-blur-xl">
          <div className="relative flex h-11 items-center justify-center px-2">
            <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[17px] font-semibold">Done</div>
          </div>
        </header>
        <main className="flex min-h-[calc(100vh-44px)] flex-col items-center justify-center px-6 pt-12">
          <div className="flex h-[88px] w-[88px] items-center justify-center rounded-full bg-[#34C759]">
            <svg viewBox="0 0 24 24" className="h-12 w-12 text-white" fill="none" stroke="currentColor" strokeWidth="3.5">
              <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="mt-6 text-center text-[28px] font-bold tracking-tight">You're all set</h1>
          <p className="mt-2 text-center text-[15px] text-[#8E8E93]">
            Your {done.tier} ${done.size}K challenge on {done.platform} is ready.
          </p>
          <p className="mt-1 text-center text-[13px] text-[#8E8E93]">Order {done.orderId}</p>
          <div className="mt-8 flex w-full max-w-[320px] flex-col gap-3">
            <button onClick={() => nav('/portal/dashboard')} className="h-[50px] rounded-[12px] bg-[#1C1C1E] text-[17px] font-semibold text-white active:scale-[0.99]">
              Go to dashboard
            </button>
            <button onClick={() => nav('/portal/orders')} className="h-[50px] rounded-[12px] bg-[#F2F2F7] text-[17px] font-semibold text-[#1C1C1E] active:scale-[0.99]">
              View order receipt
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (!tier || !size || !platform) return null;
  const sizeOpt = getSizeOption(tier, size);
  const platformOpt = getPlatformOption(platform);
  if (!sizeOpt || !platformOpt) return null;
  const tierN: TierName = tier;
  const sizeN: SizeKey = size;
  const platformN: PlatformKey = platform;
  const sizeOptN = sizeOpt;
  const platformOptN = platformOpt;

  const total = sizeOpt.price;
  const tax = 0;
  const grand = total + tax;

  async function onPay() {
    setErr(null);
    if (!cardName.trim()) return setErr('Cardholder name is required');
    if (cardNumber.replace(/\s/g, '').length < 12) return setErr('Enter a valid card number');
    if (!/^\d{2}\s*\/\s*\d{2}$/.test(cardExp)) return setErr('Card expiry must be MM/YY');
    if (cardCvc.length < 3) return setErr('Enter a valid CVC');
    const auth = getAuth();
    if (!auth?.token) return setErr('You must be signed in to start a challenge');
    setBusy(true);
    try {
      const orderRes = await API.create('Order', {
        kind: 'challenge',
        status: 'pending_payment',
        user_id: auth.user!.id,
        tier_name: tierN,
        account_size: Number(sizeN) * 1000,
        platform: platformN,
        amount: grand,
        currency: 'USD',
        product_name: `${tierN} Challenge — $${sizeN}K — ${platformOptN.displayName}`,
        description: `${tierN} tier, $${sizeN}K funded account, ${platformOptN.displayName} platform`,
      });
      const payRes = await fetch(`${API.base}/api/apps/funderpro-live/payments/challenge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
        body: JSON.stringify({ order_id: orderRes.id, amount: grand }),
      });
      const payJson = await payRes.json();
      if (!payRes.ok) throw new Error(payJson.error || 'Payment failed');
      await API.create('ActiveChallenge', {
        user_id: auth.user!.id,
        tier_name: tierN,
        size: Number(sizeN) * 1000,
        challenge_type: 'Zero Challenge',
        platform: platformN,
        platform_display: platformOptN.displayName,
        order_id: orderRes.id,
        status: 'active',
        progress_pct: 0,
        profit: 0,
        target_profit: sizeOptN.profitTarget,
        max_drawdown: sizeOptN.maxDrawdown,
        daily_drawdown: sizeOptN.dailyDrawdown,
        profit_share: sizeOptN.profitShare,
      });
      setDone({ orderId: orderRes.id, tier: tierN, size: sizeN, platform: platformOptN.displayName });
      clearDraft();
    } catch (e: any) {
      setErr(e?.message || 'Could not complete checkout');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ChallengeLayout
      current="checkout"
      subtitle={tier ? `${tier} ${size}K · ${platformOpt.displayName}` : undefined}
      footer={<IOSPrimaryButton onClick={onPay} loading={busy}>{busy ? 'Processing…' : `Pay $${grand.toFixed(2)}`}</IOSPrimaryButton>}
    >
      {/* Order summary — iOS inset-grouped list */}
      <IOSSectionHeader>Order summary</IOSSectionHeader>
      <IOSList>
        <SummaryRow label="Account size" value={`$${size}K funded`} />
        <SummaryRow label="Platform" value={platformOpt.displayName} />
        <SummaryRow label="Profit target" value={`$${sizeOpt.profitTarget.toLocaleString()}`} />
        <SummaryRow label="Profit split" value={`${sizeOpt.profitShare}% to you`} />
        <SummaryRow label="Activation fee" value="$0" />
        <SummaryRow label="Monthly data" value={sizeOpt.monthlyFeeIncluded ? 'Included' : '$30/mo'} />
        <SummaryRow label="Subtotal" value={`$${total.toFixed(2)}`} />
        <SummaryRow label="Tax" value={`$${tax.toFixed(2)}`} />
        <SummaryRow label="Total due" value={`$${grand.toFixed(2)}`} bold last />
      </IOSList>

      {/* Payment form — iOS form fields */}
      <IOSSectionHeader>Payment method</IOSSectionHeader>
      <div className="space-y-3">
        <FormField label="Cardholder name" value={cardName} onChange={setCardName} placeholder="Name on card" name="card_name" />
        <FormField label="Card number" value={cardNumber} onChange={(v) => setCardNumber(formatCardNumber(v))} placeholder="1234 1234 1234 1234" name="card_number" maxLength={19} icon="card" />
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Expiry" value={cardExp} onChange={(v) => setCardExp(formatExp(v))} placeholder="MM/YY" name="card_exp" maxLength={5} />
          <FormField label="CVC" value={cardCvc} onChange={setCardCvc} placeholder="CVC" name="card_cvc" maxLength={4} />
        </div>
        {err && (
          <div className="rounded-[10px] border border-[#FF3B30]/30 bg-[#FF3B30]/10 px-3 py-2.5 text-[13px] text-[#FF3B30]" data-testid="checkout-error">
            {err}
          </div>
        )}
      </div>

      <p className="mt-6 flex items-center justify-center gap-1.5 px-2 text-center text-[13px] text-[#8E8E93]">
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        Encrypted via Stripe. We never store your card.
      </p>
    </ChallengeLayout>
  );
}

// iOS-styled summary row — left label, right value, hairline divider
function SummaryRow({ label, value, bold, last }: { label: string; value: string; bold?: boolean; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between px-4 py-3', last ? '' : 'border-b border-[#C6C6C8]/55'].join(' ')}>
      <span className={['text-[15px] text-[#1C1C1E]', bold ? 'font-semibold' : ''].join(' ')}>{label}</span>
      <span className={['tabular-nums text-[15px] text-[#1C1C1E]', bold ? 'text-[17px] font-bold' : ''].join(' ')} data-testid={label === 'Total due' ? 'checkout-total' : undefined}>{value}</span>
    </div>
  );
}

// iOS-styled form field — label above, rounded input
function FormField({
  label, value, onChange, placeholder, name, maxLength, icon,
}: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; name: string; maxLength?: number; icon?: 'card';
}) {
  return (
    <div>
      <label htmlFor={name} className="mb-1.5 block px-1 text-[13px] font-normal text-[#3C3C43]">{label}</label>
      <div className="relative">
        <input
          id={name}
          name={name}
          type="text"
          inputMode={name === 'card_number' || name === 'card_cvc' ? 'numeric' : 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          className="w-full rounded-[10px] border border-[#C6C6C8] bg-white px-4 py-3 text-[17px] text-[#1C1C1E] placeholder:text-[#C7C7CC] outline-none transition focus:border-[#007AFF] focus:ring-0"
        />
      </div>
    </div>
  );
}

function formatCardNumber(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

function formatExp(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}
